"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { initMixpanel, setTrackingOptOut, mixpanel, isMixpanelInitialized } from "@/lib/mixpanel";
import { useSettings } from "@/lib/settings-context";
import { useLanguage } from "@/lib/language-context";
import { AnalyticsEvents } from "@/lib/use-analytics";

const MIXPANEL_USER_ID_KEY = "mixpanel_user_id";

/**
 * Provider component that initializes Mixpanel analytics.
 * Must be placed inside SettingsProvider and LanguageProvider.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { analyticsOptOut, hasCompletedOnboarding, userProfile, theme } = useSettings();
  const { targetLanguage, nativeLanguage } = useLanguage();
  const hasTrackedOpen = useRef(false);

  // Initialize Mixpanel on mount
  useEffect(() => {
    initMixpanel(analyticsOptOut);

    // Generate or retrieve anonymous user ID
    let userId = localStorage.getItem(MIXPANEL_USER_ID_KEY);
    const isFirstVisit = !userId;

    if (!userId) {
      userId = crypto.randomUUID();
      localStorage.setItem(MIXPANEL_USER_ID_KEY, userId);
    }

    // Identify user
    if (isMixpanelInitialized()) {
      mixpanel.identify(userId);

      // Register super properties
      mixpanel.register({
        target_language: targetLanguage.code,
        native_language: nativeLanguage.code,
        speaking_style: userProfile.speakingStyle || "unknown",
        theme: theme,
        onboarding_completed: hasCompletedOnboarding,
      });

      // Track app_opened event once per session
      if (!hasTrackedOpen.current) {
        mixpanel.track(AnalyticsEvents.APP_OPENED, {
          first_visit: isFirstVisit,
          returning_user: !isFirstVisit,
          target_language: targetLanguage.code,
          native_language: nativeLanguage.code,
        });
        hasTrackedOpen.current = true;
      }
    }
  }, []); // Only run once on mount

  // Update opt-out state when it changes
  useEffect(() => {
    setTrackingOptOut(analyticsOptOut);
  }, [analyticsOptOut]);

  // Update super properties when user settings change
  useEffect(() => {
    if (isMixpanelInitialized()) {
      mixpanel.register({
        target_language: targetLanguage.code,
        native_language: nativeLanguage.code,
        speaking_style: userProfile.speakingStyle || "unknown",
        theme: theme,
        onboarding_completed: hasCompletedOnboarding,
      });
    }
  }, [targetLanguage.code, nativeLanguage.code, userProfile.speakingStyle, theme, hasCompletedOnboarding]);

  return <>{children}</>;
}
